import React, { useState, useEffect } from 'react';
import TipTapEditor from './TipTapEditor';
import TagInput from './TagInput';
import BlogPreview from './BlogPreview';

interface BlogPost {
  _id: string;
  title: string;
  description: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  slug: string;
  isPublished: boolean;
  featured: boolean;
  readTime: number;
  seoTitle: string;
  seoDescription: string;
  imageUrl?: string;
  views: number;
  likes: number;
  createdAt: string;
  updatedAt: string;
}

interface BlogFormData {
  title: string;
  description: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  slug?: string;  // Optional, will be generated from title
  isPublished: boolean;
  featured: boolean;
  readTime: number;
  seoTitle: string;
  seoDescription: string;
  imageUrl: string;
  publishDate?: string; // Custom publish date
}

function BlogSection() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [authors, setAuthors] = useState<string[]>(['DUSKPROOF', 'Sorkand', 'Calvinreel']);
  const [showNewAuthorInput, setShowNewAuthorInput] = useState(false);
  const [newAuthorName, setNewAuthorName] = useState('');
  const [showRemoveMode, setShowRemoveMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    description: '',
    content: '',
    author: 'DUSKPROOF', // Default author
    category: '',
    tags: [],
    slug: '',
    isPublished: false,
    featured: false,
    readTime: 5, // Auto-calculated, not editable
    seoTitle: '',
    seoDescription: '',
    imageUrl: '',
    publishDate: new Date().toISOString().slice(0, 16) // Current date and time
  });

  // Auto-save draft to localStorage
  useEffect(() => {
    if (showForm && (formData.title || formData.content || formData.description)) {
      const draftKey = editingPost ? `blog_draft_${editingPost._id}` : 'blog_draft_new';
      localStorage.setItem(draftKey, JSON.stringify(formData));
    }
  }, [formData, showForm, editingPost]);

  // Clear draft from localStorage
  const clearDraft = () => {
    const draftKey = editingPost ? `blog_draft_${editingPost._id}` : 'blog_draft_new';
    localStorage.removeItem(draftKey);
  };

  // Add new author
  const handleAddNewAuthor = () => {
    if (newAuthorName.trim() && !authors.includes(newAuthorName.trim())) {
      const newAuthors = [...authors, newAuthorName.trim()];
      setAuthors(newAuthors);
      setFormData(prev => ({ ...prev, author: newAuthorName.trim() }));
      setNewAuthorName('');
      setShowNewAuthorInput(false);
      
      // Save to localStorage for persistence
      localStorage.setItem('blog_authors', JSON.stringify(newAuthors));
    }
  };

  // Remove author
  const handleRemoveAuthor = (authorToRemove: string) => {
    // Don't allow removing default authors
    const defaultAuthors = ['DUSKPROOF', 'Sorkand', 'Calvinreel'];
    if (defaultAuthors.includes(authorToRemove)) {
      alert('Cannot remove default authors');
      return;
    }

    const newAuthors = authors.filter(author => author !== authorToRemove);
    setAuthors(newAuthors);
    
    // If current selected author is being removed, switch to DUSKPROOF
    if (formData.author === authorToRemove) {
      setFormData(prev => ({ ...prev, author: 'DUSKPROOF' }));
    }
    
    // Save to localStorage
    localStorage.setItem('blog_authors', JSON.stringify(newAuthors));
  };

  // Reset form to default values
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      author: 'DUSKPROOF', // Default author
      category: '',
      tags: [],
      slug: '',
      isPublished: false,
      featured: false,
      readTime: 5,
      seoTitle: '',
      seoDescription: '',
      imageUrl: '',
      publishDate: new Date().toISOString().slice(0, 16) // Current date and time
    });
    setEditingPost(null);
    setShowNewAuthorInput(false);
    setNewAuthorName('');
    setShowRemoveMode(false);
  };

  // Load authors from localStorage on component mount
  useEffect(() => {
    const savedAuthors = localStorage.getItem('blog_authors');
    if (savedAuthors) {
      try {
        const parsedAuthors = JSON.parse(savedAuthors);
        if (Array.isArray(parsedAuthors)) {
          setAuthors(parsedAuthors);
        }
      } catch (error) {
        console.error('Error parsing saved authors:', error);
      }
    }
  }, []);

  // Convert local image URL to full API URL
  const convertToFullUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    
    // If it's already an external URL (http/https), return as-is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If it's a local API path like /api/images/blogs/filename, prepend backend URL
    if (imageUrl.startsWith('/api/images/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      return `${apiUrl}${imageUrl}`;
    }
    
    // If it's old path format /uploads/..., extract filename and use new format
    if (imageUrl.includes('/uploads/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const filename = imageUrl.split('/').pop() || '';
      return `${apiUrl}/api/images/blogs/${filename}`;
    }
    
    // If it's just a filename, use /api/images/blogs endpoint
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    return `${apiUrl}/api/images/blogs/${imageUrl}`;
  };

  // Fetch blog posts
  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/blogs/admin/all', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingPost ? `/api/blogs/${editingPost._id}` : '/api/blogs';
      const method = editingPost ? 'PUT' : 'POST';

      // Generate slug and readTime if not editing
      const dataToSend = {
        ...formData,
        slug: editingPost ? formData.slug : formData.title
          .toLowerCase()
          .replace(/[^a-z0-9 -]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim(),
        readTime: Math.max(1, Math.min(600, Math.ceil(formData.content.replace(/<[^>]*>/g, '').length / 1000))) || 5, // Auto-calculate read time (strip HTML tags, ~1000 chars/min)
        createdAt: formData.publishDate ? new Date(formData.publishDate).toISOString() : undefined
      };

      console.log('Sending blog data:', dataToSend);
      console.log('URL:', url);
      console.log('Method:', method);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        await fetchBlogPosts();
        clearDraft(); // Clear the saved draft
        resetForm();
        setShowForm(false);
        alert('Blog post saved successfully!');
      } else {
        const errorData = await response.text();
        console.error('Error saving blog post:', response.status, errorData);
        alert(`Failed to save blog post: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving blog post');
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/upload/blog-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Store just the API path, not the full URL
        setFormData(prev => ({
          ...prev,
          imageUrl: result.url // Store /api/images/blogs/filename
        }));
        
        console.log('Image uploaded successfully:', result.url);
        alert('Image uploaded successfully!');
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  // Handle edit
  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      description: post.description,
      content: post.content,
      author: post.author,
      category: post.category,
      tags: post.tags,
      slug: post.slug,
      isPublished: post.isPublished,
      featured: post.featured,
      readTime: post.readTime,
      seoTitle: post.seoTitle || '',
      seoDescription: post.seoDescription || '',
      imageUrl: post.imageUrl || '',
      publishDate: post.createdAt ? new Date(post.createdAt).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16)
    });
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const response = await fetch(`/api/blogs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setBlogPosts(blogPosts.filter(post => post._id !== id));
        alert('Blog post deleted successfully!');
      } else {
        const errorData = await response.text();
        console.error('Error deleting blog post:', response.status, errorData);
        alert(`Failed to delete blog post: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post');
    }
  };

  // Filter posts based on search
  const filteredPosts = blogPosts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: 'var(--dusk-bg, #0a0908)', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Blog Management</h1>
            <p className="text-gray-400">Create and manage blog posts and articles</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-gray-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            <button
              onClick={() => {
                setShowForm(!showForm);
                // Clear editing state when toggling form
                if (showForm) {
                  setEditingPost(null);
                  resetForm();
                }
              }}
              className="admin-btn admin-btn-primary"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Blog
                </>
              )}
            </button>
          </div>
        </div>

        {/* Add Blog Form */}
        {showForm && (
          <div className="p-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <h2 className="text-xl font-bold text-white mb-6">
              {editingPost ? 'Edit Blog Post' : 'Add New Blog Post'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="Blog post title"
                      required
                    />
                    {formData.title.length > 10 && !formData.seoTitle && (
                      <button
                        type="button"
                        onClick={() => {
                          const smartFill = {
                            seoTitle: formData.title.length <= 60 ? formData.title : formData.title.substring(0, 57) + '...',
                            seoDescription: formData.description || `Read our latest article: ${formData.title}`,
                            category: formData.category || 'Other'
                          };
                          setFormData({ ...formData, ...smartFill });
                        }}
                        className="admin-btn admin-btn-primary admin-btn-sm absolute right-2 top-2"
                        title="Auto-fill SEO fields based on title"
                      >
                        🤖 Smart Fill
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-300">
                    Featured Image
                  </label>
                  <div className="flex gap-2">
                    {formData.imageUrl && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                    placeholder="Upload an image or enter URL manually"
                    readOnly={formData.imageUrl?.startsWith('/uploads/') || false}
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className={`admin-btn admin-btn-primary admin-btn-sm cursor-pointer transition-colors ${
                        uploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm">Upload</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
                {formData.imageUrl && (
                  <div className="mt-2">
                    <img 
                      key={formData.imageUrl}
                      src={convertToFullUrl(formData.imageUrl) + `?t=${Date.now()}`}
                      alt="Preview" 
                      className="max-w-full h-32 object-cover rounded-lg border border-gray-600"
                      onLoad={() => console.log('✅ Image preview loaded successfully:', formData.imageUrl)}
                      onError={(e) => {
                        const fullUrl = convertToFullUrl(formData.imageUrl);
                        console.error('❌ Failed to load image preview');
                        console.error('  Original URL:', formData.imageUrl);
                        console.error('  Full URL:', fullUrl);
                        console.error('  Error:', e);
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1 break-all">{formData.imageUrl}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Author</label>
                <div className="flex gap-2">
                  <select
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  >
                    {authors.map((author) => (
                      <option key={author} value={author}>
                        {author}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewAuthorInput(!showNewAuthorInput)}
                    className="admin-btn admin-btn-primary admin-btn-sm"
                    title="Add new author"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRemoveMode(!showRemoveMode)}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${showRemoveMode ? 'bg-red-700 hover:bg-red-800' : 'bg-red-600 hover:bg-red-700'} text-white`}
                    title="Remove authors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {showRemoveMode ? 'Done' : 'Remove'}
                  </button>
                </div>
                {showNewAuthorInput && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={newAuthorName}
                      onChange={(e) => setNewAuthorName(e.target.value)}
                      placeholder="Enter new author name"
                      className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNewAuthor();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddNewAuthor}
                      className="admin-btn admin-btn-primary admin-btn-sm"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewAuthorInput(false);
                        setNewAuthorName('');
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {showRemoveMode && (
                  <div className="mt-2 p-3 bg-red-900/20 border border-red-600/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-3">Click on authors to remove them (default authors cannot be removed):</p>
                    <div className="flex flex-wrap gap-2">
                      {authors.map((author) => {
                        const isDefault = ['DUSKPROOF', 'Sorkand', 'Calvinreel'].includes(author);
                        return (
                          <button
                            key={author}
                            type="button"
                            onClick={() => !isDefault && handleRemoveAuthor(author)}
                            className={`px-3 py-1 rounded-full text-sm transition-colors ${
                              isDefault 
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                                : 'bg-red-600 hover:bg-red-700 text-white cursor-pointer'
                            }`}
                            disabled={isDefault}
                            title={isDefault ? 'Default author cannot be removed' : `Click to remove ${author}`}
                          >
                            {author} {!isDefault && '×'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Publish Date & Time</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="datetime-local"
                    value={formData.publishDate}
                    onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, publishDate: new Date().toISOString().slice(0, 16) })}
                    className="admin-btn admin-btn-primary admin-btn-sm"
                    title="Set to current date and time"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Now
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  This will set the creation date when the article is published. Leave as current time for immediate publishing.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
                <TipTapEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Start writing your article..."
                  className="mb-4"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                <TagInput
                  value={formData.tags}
                  onChange={(tags) => setFormData({ ...formData, tags })}
                  placeholder="Add tags for the article (e.g.: blockchain, tutorial, react)"
                  maxTags={8}
                />
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="Technology">Technology</option>
                    <option value="Development">Development</option>
                    <option value="Blockchain">Blockchain</option>
                    <option value="Tutorial">Tutorial</option>
                    <option value="News">News</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-dusk-copper"
                  />
                  <span className="ml-2 text-sm text-gray-300">Published</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-dusk-copper"
                  />
                  <span className="ml-2 text-sm text-gray-300">Featured</span>
                </label>
              </div>

              {/* SEO Section */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">🔍 SEO Settings</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      SEO Title
                      <span className="text-gray-500 text-xs ml-1">(recommended: 50-60 characters)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
                      maxLength={60}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="SEO title for search engines"
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      {formData.seoTitle.length}/60 characters
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      SEO Description
                      <span className="text-gray-500 text-xs ml-1">(recommended: 150-160 characters)</span>
                    </label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })}
                      maxLength={160}
                      rows={3}
                      className="w-full px-4 py-2 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                      placeholder="SEO description for search engines"
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      {formData.seoDescription.length}/160 characters
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 border-t border-gray-700">
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    className="admin-btn admin-btn-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="admin-btn admin-btn-secondary"
                  >
                    {loading ? 'Saving...' : editingPost ? 'Update Blog Post' : 'Add Blog Post'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Blog Posts Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    SERVICE
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    VISIBILITY
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredPosts.length === 0 ? (
                  <tr className="hover:bg-[rgba(208,0,104,0.08)] transition-all">
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="text-gray-400">
                        <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2" />
                        </svg>
                        <p className="text-lg font-medium">
                          {blogPosts.length === 0 ? 'No blog posts yet' : 'No posts found'}
                        </p>
                        <p className="text-sm">
                          {blogPosts.length === 0 ? 'Click "Add Blog" to create your first blog post' : 'Try adjusting your search terms'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPosts.map((post) => (
                    <tr key={post._id} className="hover:bg-[rgba(208,0,104,0.08)] transition-all">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {post.imageUrl ? (
                            <img
                              className="h-10 w-10 rounded-lg object-cover"
                              src={convertToFullUrl(post.imageUrl)}
                              alt={post.title}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.innerHTML = `
                                  <div class="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                                    <svg class="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                      <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                    </svg>
                                  </div>
                                `;
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {post.title}
                          </div>
                          <div className="text-sm text-gray-400 max-w-xs truncate">
                            {post.description}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {post.author && (
                              <span className="admin-badge admin-badge-muted">By {post.author}</span>
                            )}
                            {post.category && (
                              <span className="admin-badge admin-badge-info">{post.category}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`${
                        post.isPublished ? 'admin-badge admin-badge-success' : 'admin-badge admin-badge-warning'
                      }`}>
                        {post.isPublished ? 'active' : 'draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start space-y-1">
                        <span className={`${
                          post.isPublished ? 'admin-badge admin-badge-public' : 'admin-badge admin-badge-private'
                        }`}>
                          {post.isPublished ? 'Public' : 'Admin Only'}
                        </span>
                        {post.featured && (
                          <span className="admin-badge admin-badge-warning">
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                          className="p-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 rounded-lg transition-all"
                          title="View Page"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(post)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(post._id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Blog Preview Modal */}
      {showPreview && (
        <BlogPreview
          title={formData.title}
          content={formData.content}
          author={formData.author}
          category={formData.category}
          tags={formData.tags}
          readTime={formData.readTime}
          imageUrl={formData.imageUrl}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

export default BlogSection;
